import { create } from "zustand";
import { ThreadId, type EnvironmentId, type ScopedThreadRef } from "@t3tools/contracts";

export type SubAgentPanelView =
  | { kind: "list"; environmentId: EnvironmentId; threadId: ThreadId }
  | {
      kind: "thread";
      environmentId: EnvironmentId;
      threadId: ThreadId;
      title: string;
      prompt?: string;
    };

/** Browser-style history: `index` addresses the visible view, later entries are the forward tail. */
export interface SubAgentPanelHistory {
  entries: ReadonlyArray<SubAgentPanelView>;
  index: number;
}

export const EMPTY_SUB_AGENT_PANEL_HISTORY: SubAgentPanelHistory = { entries: [], index: -1 };

export function currentSubAgentPanelView(history: SubAgentPanelHistory): SubAgentPanelView | null {
  return history.entries[history.index] ?? null;
}

export function canGoBack(history: SubAgentPanelHistory): boolean {
  return history.index > 0;
}

export function canGoForward(history: SubAgentPanelHistory): boolean {
  return history.index >= 0 && history.index < history.entries.length - 1;
}

export function pushSubAgentPanelView(
  history: SubAgentPanelHistory,
  view: SubAgentPanelView,
): SubAgentPanelHistory {
  const entries = [...history.entries.slice(0, history.index + 1), view];
  return { entries, index: entries.length - 1 };
}

export function stepBack(history: SubAgentPanelHistory): SubAgentPanelHistory {
  return canGoBack(history) ? { ...history, index: history.index - 1 } : history;
}

export function stepForward(history: SubAgentPanelHistory): SubAgentPanelHistory {
  return canGoForward(history) ? { ...history, index: history.index + 1 } : history;
}

interface SubAgentPanelState extends SubAgentPanelHistory {
  open: (views: ReadonlyArray<SubAgentPanelView>) => void;
  push: (view: SubAgentPanelView) => void;
  back: () => void;
  forward: () => void;
  close: () => void;
}

export const useSubAgentPanelStore = create<SubAgentPanelState>()((set) => ({
  ...EMPTY_SUB_AGENT_PANEL_HISTORY,
  open: (views) => set({ entries: [...views], index: views.length - 1 }),
  push: (view) => set((state) => pushSubAgentPanelView(state, view)),
  back: () => set(stepBack),
  forward: () => set(stepForward),
  close: () => set(EMPTY_SUB_AGENT_PANEL_HISTORY),
}));

export interface SubAgentPanelWorkEntry {
  label: string;
  childThreadId?: string;
  taskPrompt?: string;
}

function listView(parent: ScopedThreadRef): SubAgentPanelView {
  return { kind: "list", environmentId: parent.environmentId, threadId: parent.threadId };
}

export function openSubAgentList(parent: ScopedThreadRef): void {
  useSubAgentPanelStore.getState().open([listView(parent)]);
}

/**
 * Opens a sub-agent transcript on top of its parent's list, so going back from
 * a transcript reached through a work-log row still lands on the list.
 */
export function openSubAgentPanelForEntry(
  workEntry: SubAgentPanelWorkEntry,
  parent: ScopedThreadRef,
): void {
  const childThreadId = workEntry.childThreadId;
  if (!childThreadId) {
    return;
  }
  useSubAgentPanelStore.getState().open([
    listView(parent),
    {
      kind: "thread",
      environmentId: parent.environmentId,
      threadId: ThreadId.make(childThreadId),
      title: workEntry.label,
      ...(workEntry.taskPrompt ? { prompt: workEntry.taskPrompt } : {}),
    },
  ]);
}
